import * as signalR from "@microsoft/signalr";

/**
 * SignalR service for real-time order status updates.
 * Used to sync status changes across multiple browser tabs.
 */
class OrderStatusSignalRService {
  constructor() {
    this.connection = null;
    this.statusChangedCallback = null;
  }

  /**
   * Establishes a connection to the OrderStatus SignalR hub.
   */
  async connect() {
    if (this.connection) {
      return;
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl("/hubs/orderstatus")
      .withAutomaticReconnect()
      .build();

    // Handle the OrderStatusChanged event from the server
    this.connection.on("OrderStatusChanged", (data) => {
      if (this.statusChangedCallback) {
        this.statusChangedCallback(data);
      }
    });

    try {
      await this.connection.start();
    } catch (err) {
      // Connection error - will retry via withAutomaticReconnect
    }
  }

  /**
   * Disconnects from the SignalR hub.
   */
  async disconnect() {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  /**
   * Subscribes to status updates for a specific order.
   * @param {number} orderId - The order ID to subscribe to.
   */
  async subscribeToOrder(orderId) {
    if (orderId && this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke("SubscribeToOrderStatus", orderId);
    }
  }

  /**
   * Unsubscribes from status updates for a specific order.
   * @param {number} orderId - The order ID to unsubscribe from.
   */
  async unsubscribeFromOrder(orderId) {
    if (orderId && this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke("UnsubscribeFromOrderStatus", orderId);
    }
  }

  /**
   * Registers a callback for when an order status changes.
   * @param {Function} callback - Function to call with status change data.
   * @returns {Function} - Unsubscribe function to remove the callback.
   */
  onStatusChanged(callback) {
    this.statusChangedCallback = callback;
    // Return unsubscribe function
    return () => {
      this.statusChangedCallback = null;
    };
  }
}

export default new OrderStatusSignalRService();
